/**
 * useSchedulerConnection
 *
 * Shared hook that surfaces the scheduler/planner connection status.
 *
 * • **Demo / tour mode** — reads from localStorage (seeded with mock data) so
 *   the full resourcing experience is shown without a backend.
 * • **Live mode** — fetches the real planner integration status from the
 *   integrations API (`getCompany`) for the currently-selected company.
 *   Falls back to `not_configured` until the API responds, and refreshes
 *   whenever the active company changes.
 *
 * The Settings → Integrations page writes to a separate integrations API, so
 * localStorage is NOT populated in live mode.  We therefore query that API
 * directly rather than relying on localStorage in live mode.
 */

import { useState, useEffect, useCallback } from 'react';
import { mockSchedulerConnection } from '@/lib/mock-scheduling-data';
import type { SchedulerConnection, SchedulerConnectionStatus } from '@/lib/mock-scheduling-data';
import { getDataMode } from '@/lib/data-service';
import { getCompany } from '@/services/integrationsRedesignService';
import { PLANNER_PRODUCT_LABELS } from '@/types/integrations-redesign';
import { useCompany } from '@/contexts/CompanyContext';

const STORAGE_KEY = 'evo_scheduler_connection';

/** Build a not_configured SchedulerConnection (safe default for live mode). */
function notConfiguredConnection(): SchedulerConnection {
  return {
    ...mockSchedulerConnection,
    status: 'not_configured' as SchedulerConnectionStatus,
  };
}

function readFromStorage(): SchedulerConnection {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as SchedulerConnection;
  } catch {
    // ignore parse errors
  }
  // In live mode, default to not_configured (real status loaded async via API)
  const mode = getDataMode();
  if (mode === 'live') {
    return notConfiguredConnection();
  }
  // Demo/tour mode — use mock data so the full experience is shown
  return mockSchedulerConnection;
}

// Custom event name for same-tab broadcasts (StorageEvent only fires in other tabs)
const STORAGE_CHANGE_EVENT = 'evo_scheduler_connection_changed';

function writeToStorage(connection: SchedulerConnection): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(connection));
    // Broadcast to same-tab listeners (StorageEvent doesn't fire in the same tab)
    window.dispatchEvent(new CustomEvent(STORAGE_CHANGE_EVENT));
  } catch {
    // ignore write errors (e.g. private browsing quota)
  }
}

// ---------------------------------------------------------------------------
// Public hook
// ---------------------------------------------------------------------------

export interface UseSchedulerConnectionReturn {
  connection: SchedulerConnection;
  isConnected: boolean;
  setConnection: (connection: SchedulerConnection) => void;
}

export function useSchedulerConnection(): UseSchedulerConnectionReturn {
  const [connection, setConnectionState] = useState<SchedulerConnection>(readFromStorage);
  const { currentCompany } = useCompany();

  // ── Live-mode: fetch real planner status from integrations API ───────────
  useEffect(() => {
    if (getDataMode() !== 'live') return;
    if (!currentCompany) {
      setConnectionState(notConfiguredConnection());
      return;
    }

    let cancelled = false;

    getCompany(currentCompany.id)
      .then(company => {
        if (cancelled) return;
        const planner = company?.plannerIntegration;
        if (planner && planner.status === 'connected') {
          const productLabel =
            planner.product
              ? (PLANNER_PRODUCT_LABELS as Record<string, string>)[planner.product] ?? planner.product
              : mockSchedulerConnection.displayName;
          setConnectionState({
            type: mockSchedulerConnection.type,
            displayName: productLabel,
            status: 'connected',
            lastSynced: planner.lastSyncAt,
          });
        } else {
          setConnectionState(notConfiguredConnection());
        }
      })
      .catch(() => {
        if (!cancelled) setConnectionState(notConfiguredConnection());
      });

    return () => { cancelled = true; };
  }, [currentCompany]);

  // ── Demo/tour: keep in sync when another component or tab writes storage ─
  useEffect(() => {
    if (getDataMode() === 'live') return;
    const handleCustom = () => setConnectionState(readFromStorage());
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY || e.key === null) {
        setConnectionState(readFromStorage());
      }
    };
    window.addEventListener(STORAGE_CHANGE_EVENT, handleCustom);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(STORAGE_CHANGE_EVENT, handleCustom);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const setConnection = useCallback((next: SchedulerConnection) => {
    writeToStorage(next);
    setConnectionState(next);
  }, []);

  return {
    connection,
    isConnected: connection.status === 'connected',
    setConnection,
  };
}

// ---------------------------------------------------------------------------
// Non-hook helper for components that can't use hooks (e.g. utility functions)
// ---------------------------------------------------------------------------

export function getIsSchedulerConnected(): boolean {
  return readFromStorage().status === 'connected';
}
